
/**
 * Module dependencies.
 */

import { Box } from 'react-components/layout';
import { Icon } from 'react-components/media';
import { IconButton } from './buttons/icon-button';
import { ReactNode } from 'react';
import { Text } from './typography';
import { color, media } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import { isString } from 'lodash';
import { useTranslate } from '../i18n';
import closeIcon from './assets/svg/16/close.svg';
import errorIcon from './assets/svg/24/error.svg';
import infoIcon from './assets/svg/24/info.svg';
import styled, { css } from 'styled-components';
import successIcon from './assets/svg/24/success.svg';
import warningIcon from './assets/svg/24/warning.svg';

/**
 * `AlertType` type.
 */

type AlertType = 'error' | 'info' | 'success' | 'warning';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  description: ReactNode;
  hasIcon?: boolean;
  id?: string;
  onClose?: () => void;
  title?: string;
  type: AlertType;
};

/**
 * Icons.
 */

const icons = {
  error: errorIcon,
  info: infoIcon,
  success: successIcon,
  warning: warningIcon
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ hasCloseButton: boolean; type: AlertType }>`
  border-radius: ${theme('dimensions.borderRadius')}px;
  column-gap: 8px;
  display: grid;
  grid-template-areas: 'icon text close';
  grid-template-columns: max-content 1fr max-content;
  padding: 12px;

  ${ifProp('hasCloseButton', css`
    padding-right: 0;
  `)}

  ${switchProp('type', {
    error: css`
      background-color: ${color('danger100')};
      color: ${color('danger')};
    `,
    info: css`
      background-color: ${color('info100')};
      color: ${color('info')};
    `,
    success: css`
      background-color: ${color('success100')};
      color: ${color('success900')};
    `,
    warning: css`
      background-color: ${color('warning100')};
      color: ${color('warning700')};
    `
  })}

  ${media.min('md')`
    padding: 16px;

    ${ifProp('hasCloseButton', css`
      padding-right: 0;
    `)}
  `}
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)`
  grid-area: icon;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ size: 'normal' })`
  color: inherit;
  margin-bottom: 4px;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ size: 'small' })`
  color: ${color('secondary')};
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)`
  align-self: center;
  grid-area: close;
  height: max-content;
  margin-right: 10px;
`;

/**
 * Export `Alert` component.
 */

export function Alert(props: Props): JSX.Element {
  const { className, description, hasIcon = true, id, onClose, title, type } = props;
  const { translate } = useTranslate();

  return (
    <Wrapper
      {...id ? { id } : {}}
      className={className}
      hasCloseButton={!!onClose}
      type={type}
    >
      {hasIcon && (
        <StyledIcon
          aria-hidden
          color={'inherit'}
          icon={icons[type]}
          size={'24px'}
        />
      )}

      <Box gridArea={'text'}>
        {title && (
          <Title>
            {title}
          </Title>
        )}

        <Description {...!isString(description) ? { as: 'div' } : {}}>
          {description}
        </Description>
      </Box>

      {onClose && (
        <StyledIconButton
          aria-label={translate('common.actions.close')}
          colorTheme={'transparent'}
          icon={closeIcon}
          iconSize={'small'}
          onClick={onClose}
          size={'small'}
        />
      )}
    </Wrapper>
  );
}
